// @ts-ignore
import { SSO_HOST } from './config'
import { message } from 'antd'
import isString from 'lodash/isString'
import isNumber from 'lodash/isNumber'
import find from 'lodash/find'
import numeral from 'numeral'
import Cookies from 'js-cookie'
import {
  CHANNEL_MAP,
  COOKIES_MAP,
  GIVEAWAY_CHANNEL,
  GLOBAL_REDIRECT_PATH,
  LOCAL_ENUM,
} from '@/constants'
import { downloadWork } from '@/services/common'
import { getStorage, getCookie, setCookie } from '@/utils/storage'
import requestHelper from '@/utils/requestHelper'
import config from '@/utils/config'
import dayjs from '@/utils/dayjs'

// 引入顺序不能改变否则打包报错（2024-4-26 因格式化报错）

/**
 * 判断是否是服务端
 */
export const isServer = () => Object.prototype.toString.call(global.process) === '[object process]'

/**
 * Query which layout should be used for the current path based on the configuration.
 * @param   {layouts}     layouts   Layout configuration.
 * @param   {pathname}    pathname  Path name to be queried.
 * @return  {string}   Return frist object when query success.
 */
export function queryLayout(layouts: any, pathname: any) {
  let result = 'public'

  const isMatch = (regepx: any) => {
    return regepx instanceof RegExp
      ? regepx.test(pathname)
      : // @ts-ignore
        pathMatchRegexp(regepx, pathname)
  }

  for (const item of layouts) {
    let include = false
    let exclude = false
    if (item.include) {
      for (const regepx of item.include) {
        if (isMatch(regepx)) {
          include = true
          break
        }
      }
    }

    if (include && item.exclude) {
      for (const regepx of item.exclude) {
        if (isMatch(regepx)) {
          exclude = true
          break
        }
      }
    }

    if (include && !exclude) {
      result = item.name
      break
    }
  }

  return result
}

/**
 * 阻塞线程
 * @param duration
 * @returns {Promise<unknown>}
 */
export async function sleep(duration: any) {
  return new Promise(resolve => {
    setTimeout(resolve, duration)
  })
}

/**
 * 获取sso跳转链接
 * @param url
 * @returns {string|string|*}
 */
export function getSSORedirectUrl(url: any) {
  if (!window.location.href) {
    return SSO_HOST
  }
  const redirectUrl = url || window.location.href
  const currentHost = process.env.NODE_ENV === 'development' ? SSO_HOST : SSO_HOST
  return `${currentHost}/?redirect=${encodeURIComponent(redirectUrl)}`
}

/**
 * 跳转sso
 * @param url
 * @returns {string|string|*}
 */
export const goSSO = (url: string) => {
  if (isServer()) return
  // if (url.includes('?')) {
  //   // eslint-disable-next-line no-param-reassign
  //   url = `${url}&channel=${CHANNEL_MAP.SSO}`
  // } else {
  //   // eslint-disable-next-line no-param-reassign
  //   url = `${url}?channel=${CHANNEL_MAP.SSO}`
  // }
  window.location.href = getSSORedirectUrl(
    url || `${window.location.origin}?channel=${CHANNEL_MAP.SSO}`,
  )
}

/**
 * 根据域名显示sso登录按钮
 */
export function ssoButtonVisible() {
  if (isServer()) return false
  return getStorage('channel') || window.location.href?.includes('channel=sso')
}

/**
 * 获取登录状态
 * @returns {boolean}
 */
export function getLoginStatus() {
  if (isServer()) return false
  return !!getCookie(COOKIES_MAP.TOKEN)
}

/**
 * 获取用户信息
 */
export function getUserInfo() {
  if (isServer()) return {}
  return JSON.parse(getStorage('user') || '{}')
}

export class Deferred<T> {
  promise: Promise<T>

  resolve!: (value: T | PromiseLike<T>) => void

  reject!: (reason?: any) => void

  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve
      this.reject = reject
    })
  }
}

const canvaSdkUrl = 'https://sdk.canva.cn/designbutton/v2/api.js'
const canvaCssLink = 'https://sdk.canva.cn/designbutton/v2/api.css'
/**
 * 动态加载可画sdk
 */
export function dynamicImportCanvaScript() {
  return new Promise((resolve: any) => {
    if (isServer()) return
    window.Canva = null
    // 删除上次加载script
    // @ts-ignore
    const scripts: any = document.getElementsByTagName('script')
    const canvaScript: any = [...scripts].find((item: any) => item.src === canvaSdkUrl)
    if (canvaScript) {
      canvaScript.parentNode.removeChild(canvaScript)
    }
    // 删除上次加载css
    // @ts-ignore
    const links: any = document.getElementsByTagName('script')
    const canvaLink: any = [...links].find((item: any) => item.href === canvaCssLink)
    if (canvaScript) {
      canvaLink.parentNode.removeChild(canvaLink)
    }
    // 创建新的script
    const newCanvaScript: any = document.createElement('script')
    newCanvaScript.type = 'text/javascript'
    newCanvaScript.src = canvaSdkUrl
    newCanvaScript.onload = () => resolve()
    document.head.appendChild(newCanvaScript)
  })
}

/**
 * 获取dom元素计算属性
 * @param element
 * @param cssPropertyName
 */
export function getStyle(element: any, cssPropertyName: string) {
  if (isServer()) return
  // @ts-ignore
  if (window.getComputedStyle) {
    // eslint-disable-next-line consistent-return
    return (window as any).getComputedStyle(element)[cssPropertyName]
  }
  // eslint-disable-next-line consistent-return
  return element.currentStyle[cssPropertyName]
}

/**
 * 格式化字典数据
 * @param dictData
 */
export function formatDict(dictData) {
  if (!Array.isArray(dictData)) {
    return {}
  }
  return dictData.reduce((prev, next) => {
    const currentCode = next.dictCode
    if (currentCode && !prev[currentCode]) {
      return {
        ...prev,
        [currentCode]: next.dictItemList,
      }
    }
    return prev
  }, {})
}

/**
 * 生成唯一id字符串
 * @returns {string}
 */
export function getUniqueId() {
  const s = []
  const hexDigits = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  for (let i = 0; i < 36; i++) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1)
  }
  s[14] = '4'
  // @ts-ignore
  s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1)
  s[8] = s[13] = s[18] = s[23] = '-'
  return s.join('')
}

/**
 * 模板详情千分位处理
 * 只处理字符串或者数字类型
 * value 需要格式的数据
 * formatStr 格式类型
 * unit 单位
 * empty 0
 */
export function numeralFun({
  value,
  formatStr,
  empty = 0,
}: {
  value?: any
  formatStr?: string
  empty?: any
}) {
  if (!value) {
    return empty
  }
  let valueRightLen = 0
  let currentFormat = formatStr
  if ((isNumber(value) || isString(value)) && !formatStr) {
    valueRightLen = `${value}`?.replace?.(/[a-zA-Z]/, '')?.split?.('.')?.[1]?.length || 0
    let str = ''
    for (let i = 0; i < valueRightLen; i++) {
      str += 0
    }
    currentFormat = `0,0.${str}`
  }

  if (isString(value)) {
    const unitReg = /[a-zA-Z]/g
    const unit: any = unitReg.exec(value)?.pop?.() || ''
    const num: any = (value as any).replace?.(unit, '') * 1
    return numeral(num).format(currentFormat) + unit
  }
  if (isNumber(value)) {
    return numeral(value).format(currentFormat)
  }
  return value
}

// 批量下载
export function batchDownloadWork({ files }: { files: any[] }, t?: (k: string) => string) {
  // @ts-ignore
  return downloadWork({ files })
    .then(({ data }) => {
      if (data.code !== 0) {
        message.error(data.message)
      } else {
        const { key } = data.result
        if (!key) return
        if (t) {
          setTimeout(() => message.success(t('components:t224')), 200)
        } else {
          setTimeout(() => message.success('开始下载'), 200)
        }
        openUrl(`${window?.APP_CONFIG?.API_HOST}/v4/demand/file/batch/zip/download?key=${key}`)
      }
    })
    .catch(e => {
      console.error('下载失败: ', e)
      if (t) {
        t('components:t225')
      } else {
        message.error('下载失败')
      }
    })
}

/**
 *
 * @param fileList 文件列表
 * @param analyze 是否包含解析状态的校验
 * @returns
 */
export const validateFileStatus = (fileList: any[] = [], analyze = true) => {
  const fs = fileList.filter(f => ['uploading', 'analyze', 'done'].includes(f.status))
  if (fs.length === 0) {
    message.warn('请选择文件上传')
    return false
  }
  if (fs.filter(f => f.status === 'uploading').length > 0) {
    message.warn('文件正在上传中')
    return false
  }
  if (fs.filter(f => f.status === 'analyze').length > 0 && analyze) {
    message.warn('文件正在解析中')
    return false
  }
  return true
}

/**
 * 动态创建script
 * @param url
 * @param slot
 * @returns
 */
export async function loadScript(url: string, position = 'head', slot?: (ele: any) => void) {
  return new Promise(resolve => {
    const element = document.createElement('script')
    element.src = url
    element.type = 'text/javascript'
    element.async = true
    slot?.(element)
    element.onload = () => resolve(true)
    document[position].appendChild(element)
  })
}
// { a:1,b:2}格式化成?a=1&b=2
export const stringifyUrlQuery = queryObj => {
  let queryString = ''
  Object.keys(queryObj).forEach((e, i) => {
    const str = `${i === 0 ? '?' : '&'}${e}=${queryObj[e]}`
    queryString += str
  })
  return queryString
}

/**
 * 带二级路由，受权限设置后，需要改成动态的url 如：媒体中心、数据中心、企业管理
 */

export const getDynamicPath = (authorizedList: any, code: string) => {
  const record = find(authorizedList, { code })
  const routeRecord = find(record?.menuPermissionList, { hasPermission: true })
  return routeRecord?.url || ''
}

/**
 * 打开url(防广告插件)
 * @param url
 */
export const openUrl = (url: string) => {
  if (!url) return;
  // setTimeout(() => window.open(url), 1);

  const a = document.createElement('a')

  a.href = url
  a.target = '_blank'
  a.rel = 'noopener norefferrer'
  a.click()

  // a.setAttribute('href', url);
  // a.setAttribute('style', 'display:none');
  // a.setAttribute('target', '_blank');
  // a.setAttribute('rel', 'noopener norefferrer');
  // document.body.appendChild(a);
  // a.click();
  // a.parentNode.removeChild(a);
}

// 获取协作系统跳转链接
export async function getXZUrl(
  params: { cf_id?: string; displayMode?: 'carousel' | 'single' } = {},
) {
  return requestHelper
    .post('v4/demand/account/generate/cmp/token', {})
    .then(({ data }: any) => {
      if (data.code !== 0) {
        return
      }
      // @ts-ignore
      params.k = data.result.accessToken
      // @ts-ignore
      return `${config.XZ_APP_HOST}/file${stringifyUrlQuery(params)}`
    })
    .catch(err => {
      console.log(err, 'err')
      message.error('获取协作平台链接失败')
    })
}

/**
 * 判断是否手机
 * @param userAgent
 * @returns
 */
export const isMobilePage = (userAgent: string) => {
  const reg: any =
    /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i
  return userAgent.match(reg) ? true : false
}

// a 标签下载
export const tagADownload = (url: string) => {
  if (!url) {
    return
  }
  const id = 'cb_ai_designer_download'
  const a = document.createElement('a')
  a.style.display = 'none'
  a.target = '_blank'
  a.id = id
  a.href = url
  document.body.appendChild(a)
  a.click() // 自动触发点击a标签的click事件
  document.body.removeChild(document.getElementById(id))
}

/**
 * 服务端重定向参数配置
 * @param redirect
 * @param props
 * @returns
 */
export const getRedirectProps = (redirect: any = {}, props: any = {}) => {
  return {
    redirect: {
      destination: '/error-page',
      permanent: false,
      ...(redirect || {}),
    },
    props: props || {},
  }
}

/**
 * 获取ai生成url
 * @param params
 * @returns
 */
export async function getAIGeneratorUrl(
  params: { cf_id?: string; displayMode?: 'carousel' | 'single' } = {},
) {
  return requestHelper
    .post('v4/demand/account/generate/cmp/token', {})
    .then(({ data }: any) => {
      if (data.code !== 0) {
        return
      }
      // @ts-ignore
      params.k = data.result.accessToken
      // @ts-ignore
      return `${config.XZ_APP_HOST}/ai-generator${stringifyUrlQuery(params)}`
    })
    .catch(err => {
      console.log(err, 'err')
      message.error('获取协作平台链接失败')
    })
}

/**
 * 全通通用重定向地址(默认模板列表页面)
 * @param isServerSide
 * @param v
 */
export function getGlobalRedirectPath(isServerSide: boolean = false, v?: string) {
  if (isServerSide) {
    // 服务端
    if (v === GIVEAWAY_CHANNEL.GOOGLE) {
      // 谷歌推广渠道过来的重定向到模板列表页面
      return '/inspiration/template'
    }
    return GLOBAL_REDIRECT_PATH
  }
  // 客户端
  if (Cookies.get(COOKIES_MAP.GIVEAWAY_CHANNEL) === GIVEAWAY_CHANNEL.GOOGLE) {
    // 谷歌推广渠道过来的重定向到模板列表页面
    return '/inspiration/template'
  }
  return GLOBAL_REDIRECT_PATH
}

/**
 * 格式化金额
 * @param price
 * @returns
 */
export const numeralFormat = (price: number) => {
  if (!price) {
    return '0.00'
  }
  return numeral(price).format('0,0.00')
}

/**
 * 设置编辑器跳转url
 */
export const setEditPageFromUrl = () => {
  Cookies.set(COOKIES_MAP.EIDT_PAGE_FROM, encodeURIComponent(location.href || ''))
}

/**
 * header新增参数
 * @param headerParams
 * @param headers
 * @returns
 */
export function addHeaderParams(headerParams: any = {}, req: any = {}) {
  const headers = req?.headers || {}
  const cookies = req?.cookies || {}
  const accessToken: any = cookies?.[COOKIES_MAP.TOKEN]
  const newHeaderParams: any = { ...headerParams }
  if (!headerParams?.accessToken && accessToken) {
    newHeaderParams.accessToken = accessToken
  }
  if (headers['x-gray-tag']) {
    newHeaderParams['x-gray-tag'] = headers['x-gray-tag']
  }
  if (headers['fix-user-header-date']) {
    newHeaderParams['fix-user-header-date'] = headers['fix-user-header-date']
  }
  if (headers['fix-canva-user-id']) {
    newHeaderParams['fix-canva-user-id'] = headers['fix-canva-user-id']
  }
  // 语言
  const lang = cookies[COOKIES_MAP.LANGUAGE] || cookies[COOKIES_MAP.ACCEPT_LANGUAGE]
  if (lang) {
    newHeaderParams['X-CB-Locale'] = lang
  }
  return {
    headers: newHeaderParams,
  }
}

/**
 * 根据近xx天获取日期区间
 * @param day
 * @param format
 */
export function getDateRangeByDays(day: number, format: string = 'YYYY-MM-DD') {
  if (!day) {
    return [[], []]
  }
  const startDate = dayjs().startOf('day').subtract(day, 'days')
  const startDateString = dayjs(startDate).format(format)
  const endDate = dayjs().endOf('day')
  const endDateString = dayjs(endDate).format(format)
  return [
    [startDate, endDate],
    [startDateString, endDateString],
  ]
}

/**
 * 获取缓存的浏览器语言
 */
export function getAcceptLocale() {
  return getCookie(COOKIES_MAP.ACCEPT_LANGUAGE) || ''
}

/**
 * 设置浏览器系统语言
 * @param locale
 */
export function setAcceptLocale(locale: string) {
  // 目前只支持中英文
  const v = locale?.includes('zh') ? LOCAL_ENUM.ZH_CN : LOCAL_ENUM.EN_US
  setCookie(v, COOKIES_MAP.ACCEPT_LANGUAGE)
}

/**
 * 获取用户设置的语言
 */
export function getUserLocale() {
  return getCookie(COOKIES_MAP.LANGUAGE)
}

/**
 * 设置登录态下的用户语言
 * @param locale
 */
export function setUserLocale(locale: string) {
  // 目前只支持中英文
  const v = locale?.includes('zh') ? LOCAL_ENUM.ZH_CN : LOCAL_ENUM.EN_US
  setCookie(v, COOKIES_MAP.LANGUAGE)
}

/**
 * 获取当前系统语言
 */
export function getSystemLocale() {
  if (getLoginStatus()) {
    // 已登录
    return getUserLocale() || getAcceptLocale()
  }
  return getAcceptLocale()
}

/**
 * JSON.parse
 * @param v
 * @returns
 */
export function jsonParse(v: string) {
  try {
    return JSON.parse(v)
  } catch (e) {
    console.error('解析失败')
    return null
  }
}


/**
 * 全局格式化用户信息
 * @param userInfo
 * @returns
 */
export function formatUserInfo(userInfo: any) {
  const { isSso, ...restUserInfo } = userInfo || {};
  return {
    ...restUserInfo,
    isBindSSO: !!isSso, // 是否关联了sso账号(sso账号登录时为true, passport账号登录时也可能为true)
    avatar: userInfo.avatar || config.defaultAvatar,
  }
}
